// Pieces the price-list import's screens share (UC-IMPORTS-001..006) and the screens that link to an import — a list's layout and
// "Newer list available" (UC-PRICING-009), an Import entry in the price history (UC-PRICING-003), a lab line's or a product's
// source (UC-PURCHASING-006 A6, UC-CATALOGUE-003 A9): who may import (BR-IMPORTS-001), the words of a status, a verdict, a field,
// a line's error and a run's failure, and one import read once by its id.
import type { ImportJobStatus, ImportJobView, ImportMappingView, ImportRowError, ImportRowVerdict, PriceListMappingField } from "@visionx/contracts";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { importJobs } from "../api/endpoints.imports";
import { formatCount, formatDate, isLocalDate } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { Chip, type Tone } from "../ui/bits";

/** A calendar day `YYYY-MM-DD` as the screens write it (PricingShared's `dayText`, kept here so pricing's screens may link to an import). */
const dayText = (day: string | null | undefined): string => (day && isLocalDate(day) ? formatDate(`${day}T12:00:00.000Z`) : "—");

/**
 * BR-IMPORTS-001: a price-list import is the Owner's and an Admin's — `imports.job.*` with `pricing.price_list.write` — and saving a
 * mapping profile needs `imports.profile.write`. `read` opens an import; `write` uploads, maps, checks, ticks, applies and discards.
 */
export function useImportAccess(): { read: boolean; write: boolean; profiles: boolean } {
  const s = useSessionView();
  const kind = can(s, "pricing.price_list.write");
  return { read: kind && can(s, "imports.job.read"), write: kind && can(s, "imports.job.write"), profiles: can(s, "imports.profile.write") };
}

/** An import that is neither applied nor discarded: a newer list waits (UC-IMPORTS-001 step 9). */
export const PENDING_STATUSES: readonly ImportJobStatus[] = ["Uploaded", "Checking", "Checked", "Importing", "Failed"];

const STATUS_TONE: Readonly<Record<ImportJobStatus, Tone>> = {
  Uploaded: "neutral", Checking: "info", Checked: "info", Importing: "info", Imported: "ok", Failed: "crit", Discarded: "neutral",
};
/** Whether an import's Apply ever started — it was committed, or applied (BR-IMPORTS-020). */
export const applyStarted = (j: Partial<Pick<ImportJobView, "commit" | "appliedAt">>): boolean => (j.commit ?? null) !== null || (j.appliedAt ?? null) !== null;

/**
 * A Failed import whose check stopped, not its Apply: the check's own failure, or the organisation's suspension stopping a check
 * before any Apply started (phase `suspended`; BR-IMPORTS-022) — that one is checked again, not resumed (its moves say `check`).
 */
export function checkStopped(j: Pick<ImportJobView, "status" | "failure"> & Partial<Pick<ImportJobView, "commit" | "appliedAt">>): boolean {
  if (j.status !== "Failed") return false;
  return j.failure?.phase === "check" || (j.failure?.phase === "suspended" && !applyStarted(j));
}

/** BR-IMPORTS-004: a status in words — a failed import says whether its check or its Apply stopped. */
export function statusWords(j: Pick<ImportJobView, "status" | "failure"> & Partial<Pick<ImportJobView, "commit" | "appliedAt">>): string {
  if (j.status === "Failed") return checkStopped(j) ? "Check stopped" : "Import stopped";
  return j.status;
}
export function ImportStatusChip({ job }: { job: Pick<ImportJobView, "status" | "failure"> & Partial<Pick<ImportJobView, "commit" | "appliedAt">> }) {
  return <Chip tone={STATUS_TONE[job.status]} dot>{statusWords(job)}</Chip>;
}

const VERDICT_TONE: Readonly<Record<ImportRowVerdict, Tone>> = { New: "ok", Changed: "info", Unchanged: "neutral", Discontinued: "warn", Error: "crit" };
export function VerdictChip({ verdict }: { verdict: ImportRowVerdict }) {
  return <Chip tone={VERDICT_TONE[verdict]}>{verdict}</Chip>;
}

/** BR-IMPORTS-005: a price-list field as the mapping step names it, in the order it offers them. */
export const FIELD_LABEL: Readonly<Record<PriceListMappingField, string>> = {
  supplierCode: "Supplier lens code", labPrice: "Lab price", description: "Description", recommendedRetail: "Recommended retail price", saoaCode: "SAOA lens code",
  range: "Range", index: "Index", design: "Design", material: "Material", diameter: "Diameter",
  sphereMin: "Sphere from", sphereMax: "Sphere to", cylinderMin: "Cylinder from", cylinderMax: "Cylinder to", addMin: "Add from", addMax: "Add to",
};

/** `price-import`'s stepper line: "code → supplier lens code · description → product · … · lab price → lab price ex VAT", in file order. */
export function mappedSummary(mapping: ImportMappingView, header: readonly string[]): string {
  const target = (f: PriceListMappingField): string => {
    if (f === "description") return "product";
    if (f === "labPrice") return mapping.pricesIncludeVat ? "lab price incl VAT" : "lab price ex VAT";
    if (f === "recommendedRetail") return mapping.recommendedRetailIncludesVat ? "recommended retail incl VAT" : "recommended retail ex VAT";
    return FIELD_LABEL[f].toLowerCase().replace("saoa", "SAOA");
  };
  const byKey = new Map(Object.entries(mapping.columnMap).map(([c, f]) => [c.trim().toLowerCase(), [c, f] as const]));
  const ordered = header.map((h) => byKey.get(h.trim().toLowerCase())).filter((x): x is readonly [string, PriceListMappingField] => x !== undefined);
  const rest = [...byKey.values()].filter(([c]) => !header.some((h) => h.trim().toLowerCase() === c.trim().toLowerCase()));
  return [...ordered, ...rest].map(([c, f]) => `${c} → ${target(f)}`).join(" · ");
}

/** A power range's highest end and its lowest (BR-CATALOGUE-008). */
const RANGE_START: Readonly<Record<string, PriceListMappingField>> = { sphereMax: "sphereMin", cylinderMax: "cylinderMin", addMax: "addMin" };

/** A line's error in words (BR-IMPORTS-007): the common ones said plainly, the rest as the server words them. */
export function errorWords(e: ImportRowError): string {
  const field = e.field === "labPrice" ? "Lab price" : e.field === "recommendedRetail" ? "Recommended retail price" : e.field === "supplierCode" ? "Supplier lens code" : e.field === "sku" ? "The new product's SKU" : FIELD_LABEL[e.field as PriceListMappingField] ?? e.field;
  switch (e.code) {
    case "negative": return `${field} is negative`;
    case "too_many_decimals": return `${field} has more than two decimals`;
    case "not_a_number": return `${field} is not a number`;
    case "required": return e.field === "supplierCode" && /SAOA/.test(e.message) ? `No supplier lens code, and ${e.message.replace(/^no code, and /, "")}` : `${field} is missing`;
    case "duplicate_in_file": return `The code is also on row${(e.rows?.length ?? 0) > 1 ? "s" : ""} ${(e.rows ?? []).join(", ")} — none of them is imported`;
    case "too_long": return `${field} is too long — ${e.message}`;
    case "out_of_range": return `${field} is out of range — ${e.message}`;
    // A range's highest end below its lowest (BR-IMPORTS-007, BR-CATALOGUE-008): "Sphere to is below Sphere from".
    case "below_min": return `${field} is below ${FIELD_LABEL[RANGE_START[e.field] ?? (e.field as PriceListMappingField)] ?? "its start"} — ${e.message}`;
    default: return `${field}: ${e.message}`;
  }
}

/** UC-IMPORTS-003 E2, UC-IMPORTS-004 E7–E9: why a run stopped, in words, from its recorded reason. */
export function failureWords(job: Pick<ImportJobView, "failure" | "supplier"> & Partial<Pick<ImportJobView, "commit" | "appliedAt">>): string {
  const reason = job.failure?.reason ?? "";
  const list = job.supplier ? `${job.supplier.name}'s current list` : "The supplier's current list";
  if (reason.startsWith("list_changed")) return `${list} changed while it ran, so nothing from it became current. Discard this import and upload the file again.`;
  if (reason.startsWith("suspended") || job.failure?.phase === "suspended") {
    // A check stopped before any Apply is checked again; a run that had started is resumed (BR-IMPORTS-022; the moves say which).
    return applyStarted(job)
      ? "The organisation was suspended, so the run stopped before its next step. Resume it once the organisation is active again."
      : "The organisation was suspended, so the check stopped before its next step. Check the file again once the organisation is active again.";
  }
  if (reason.startsWith("refused")) return `A line was refused when it was written: ${reason.replace(/^refused:\s*/, "")}.`;
  return reason ? `The reason recorded: ${reason.replace(/\.$/, "")}.` : "It stopped before it finished.";
}

/**
 * "All branches" or the one branch an import applies to (BR-IMPORTS-015) — at a practice of one store nothing for the
 * organisation's list, which its one branch takes, and a branch's own list still named (UC-IMPORTS-005 A8; change 13).
 */
export const scopeWords = (j: Pick<ImportJobView, "scope" | "branch">, oneStore = false): string | null =>
  (j.scope === "AllBranches" ? (oneStore ? null : "All branches") : `${j.branch?.name ?? "One branch"} only`);

/** "list dated 05 Sept 2026". */
export const listDatedWords = (j: Pick<ImportJobView, "listDate">): string => (j.listDate ? `list dated ${dayText(j.listDate)}` : "no list date");

/**
 * BR-IMPORTS-018: what Apply made of the lines, as the history and the result say it — the discontinued lines flagged, and of
 * them the lenses its final write hid from new orders and, where a list had hid some, those it offered again (BR-IMPORTS-010;
 * DVN's answer of 24 Oct 2026, the board's question 4; the review of 24 Oct 2026). An import applied before the lenses were
 * counted says only what it flagged.
 */
export function outcomeWords(o: NonNullable<ImportJobView["outcome"]>): string {
  const lenses = [
    o.hidden !== null ? `${formatCount(o.hidden)} hidden from new orders` : null,
    o.broughtBack ? `${formatCount(o.broughtBack)} brought back to new orders` : null,
  ].filter((w): w is string => w !== null);
  return [
    `${formatCount(o.created)} created`, `${formatCount(o.changed)} changed`, `${formatCount(o.unchanged)} unchanged`, `${formatCount(o.skipped)} skipped`,
    `${formatCount(o.errored)} errored`, `${formatCount(o.flagged)} flagged`, ...lenses,
  ].join(" · ");
}

/** One import read once by its id — for a line's, a product's or a history entry's source — only by someone who may open it. */
export function useImportJob(id: string | null | undefined) {
  const access = useImportAccess();
  return useQuery({ queryKey: ["import", id ?? ""], queryFn: () => importJobs.get(id!), enabled: access.read && !!id, staleTime: 60_000 });
}

/** The imports waiting to be applied — a newer list for their supplier and scope (UC-PRICING-009 step 1) — for someone who may open them. */
export function usePendingImports() {
  const access = useImportAccess();
  return useQuery({ queryKey: ["imports", "pending"], queryFn: () => importJobs.list({ status: PENDING_STATUSES }, { limit: 200 }), enabled: access.read, staleTime: 30_000 });
}

/** A link to an import for someone who may open it (BR-IMPORTS-001), plain text for anyone else. */
export function ImportLink({ id, children }: { id: string; children: ReactNode }) {
  const access = useImportAccess();
  return access.read ? <Link to={`/price-import/${id}`}>{children}</Link> : <>{children}</>;
}

/** A price-history entry an import wrote (BR-IMPORTS-017, UC-PRICING-003 step 2): "Seiko Optical SA · list dated 05 Sept 2026", linked. */
export function ImportSource({ importJobId }: { importJobId: string }) {
  const job = useImportJob(importJobId);
  const v = job.data?.data;
  if (!v) return null;
  return <div className="small faint"><ImportLink id={importJobId}>{v.supplier?.name ?? v.file.name} · {listDatedWords(v)}</ImportLink></div>;
}

/**
 * "imported from Seiko_SA_pricelist_2026-09.csv, row 8" (BR-IMPORTS-017): the file's name as the source gives it, else as the
 * import gives it to someone who may open it, else "a price-list import"; linked to the import for someone who may open it.
 */
export function ImportedFrom({ importJobId, fileName, rowNo, prefix = "imported from" }: { importJobId: string; fileName?: string | null | undefined; rowNo: number | null; prefix?: string | undefined }) {
  const job = useImportJob(fileName ? null : importJobId);
  const name = fileName ?? job.data?.data.file.name ?? null;
  return <>{prefix} <ImportLink id={importJobId}>{name ?? "a price-list import"}{rowNo !== null ? `, row ${rowNo}` : ""}</ImportLink></>;
}
